import React from 'react';
import PropTypes from 'prop-types';
import PageAncestor from 'core/common/page/pageAncestor';
import Tabs from 'material-ui/Tabs/Tabs';
import Tab from 'material-ui/Tabs/Tab';
import {MODULE, PERMISSION, PageProps} from "core/metamodel/metaConstant";
import {observer, Observer} from "mobx-react";
import last = require("lodash/last");
import Route = ReactRouter.Route;
import {observable} from "mobx";
import mdAxios from 'core/common/config/axios-config';
import {OBJECTMAP} from "core/common/domain/commonDomain";
import {formatNumber} from "core/common/utils/numberUtils";
import {CacheDto} from "administration/server/serverDomain";
import {SecuredZzFlatButton, ZzFlatButton} from "core/components/buttons/buttons";
import {formatDateTime} from "core/common/utils/dateUtils";


interface InfoMetricts  {
  info: OBJECTMAP<any>;
  zzInfo: OBJECTMAP<any>;
}

const MValue = ({label, value, numFormat, suffix}: { label: string, value: any, numFormat?: string, suffix?: string }) => {

  const valueStyle = {display: 'table-cell'};
  if (numFormat) {
    value = formatNumber(value, numFormat);
    valueStyle['textAlign'] = 'right';
  }

  return (
    <div style={{display: 'table-row'}}>
      <div style={{display: 'table-cell', paddingRight: 10}}>{label}</div>
      <div style={valueStyle}>{value}{suffix}</div>
    </div>
  )
};

// {"memory":{"Max memory":"3,5 GiB","Free memory":"535,7 MiB","Total memory":"905,5 MiB"},"jvmProps":[{"java.vendor":"Oracle Corporation"},{"sun.java.launcher":"SUN_STANDARD"},

const Metrics = ({zzInfo, info}: InfoMetricts) => (
  <div>
    <div className="row">
      <div className="col-xs-12 col-sm-6">
        <h3>Build info</h3>
        <MValue label="Version" value={ info.build ? info.build.version : ""}/>
        <MValue label="Timestamp" value={formatDateTime(info.build ? info.build.time : "")} />
      </div>
      <div className="col-xs-12 col-sm-6">
        <h3>Memory</h3>
        {Object.entries(zzInfo["memory"]).map( entry => <MValue label={entry[0]} value={entry[1]}/>)}
      </div>
    </div>
    <div className="row">
      <div className="col-sm-12">
        <h3>JVM Properties</h3>
        {zzInfo["jvmProps"].map( prop => {
          let key = Object.keys(prop)[0];
            return <MValue label={key} value={prop[key]}/>
          })
        }
      </div>
    </div>
  </div>
);

const Logs = ({data}: { data: string[] }) => (
  <table className="zz-simple-table" style={{margin: 20}}>
    <tbody>
    {data.map((logName) => (
      <tr key={logName}>
        <td>
          <a href={'/api/core/logging/' + logName} target="_blank">
            {logName}
          </a>
        </td>
      </tr>
    ))}
    </tbody>
  </table>
);


@observer
class CacheStats extends React.Component<any, any> {

  @observable private cacheStats: CacheDto[];


  componentDidMount() {
    return this.readData();
  }

  readData() {
    return mdAxios.get("/core/caches")
      .then((response) => {
        this.cacheStats = response.data;
      });
  }

  onClearAll = () => {
    return mdAxios.delete("/core/caches").then(response => {
      return this.readData();
    });
  };

  onClear = (cacheName: string) => {
    return mdAxios.delete("/core/cache/" + cacheName).then(response => {
      return this.readData();
    });
  };

  onRefresh = () => {
    return this.readData();
  };


  render() {

    if (!this.cacheStats) return <noscript/>;

    return (
      <div>
        <div style={{display: 'flex'}}>
        <SecuredZzFlatButton onClick={this.onClearAll} label="Clear all caches" primary
                             style={{margin: '20px 0 0 20px'}} permission={PERMISSION.Update}/>
        <ZzFlatButton onClick={this.onRefresh} label="Refresh" primary style={{margin: '20px 0 0 20px'}}/>
        </div>
        <table className="zz-simple-table" style={{margin: 20}}>
          <thead>
          <tr>
            <th>Cache Name</th>
            <th>Clear</th>
            <th>Current size</th>
            <th>Total size</th>
            <th>Puts #</th>
            <th>Gets #</th>
            <th>Hits #</th>
            <th>Misses #</th>
            <th>Remove hits #</th>
            <th>Remove misses #</th>
            <th>Evictions #</th>
            <th>Read time</th>
            <th>Write time</th>
            <th>Remove time</th>
          </tr>
          </thead>
          <tbody>
          {this.cacheStats.map((cacheDto) => (
            <tr key={cacheDto.name}>
              <td>{cacheDto.name}</td>
              <td>
                <SecuredZzFlatButton onClick={this.onClear.bind(this, cacheDto.name)} label="Clear" primary
                                     permission={PERMISSION.Update}/>
              </td>
              <td>{cacheDto.currentNumberOfEntries}</td>
              <td>{cacheDto.totalNumberOfEntries}</td>
              <td>{cacheDto.stores}</td>
              <td>{cacheDto.retrievals}</td>
              <td>{cacheDto.hits}</td>
              <td>{cacheDto.misses}</td>
              <td>{cacheDto.removeHits}</td>
              <td>{cacheDto.removeMisses}</td>
              <td>{cacheDto.evictions}</td>
              <td>{cacheDto.averageReadTime}</td>
              <td>{cacheDto.averageWriteTime}</td>
              <td>{cacheDto.averageRemoveTime}</td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    );
  }
}


@observer
export default class ServerOverview extends PageAncestor<PageProps, undefined> {

  static module = MODULE.core;
  static title = 'Server';
  static icon = 'server';
  static rootPage = true;
  static protectedPage = true;


  static fetchData(routerParams) {
    console.log("ServerOverview#fetchData %O", routerParams);
    // return Promise.all([gridService.fetchGrids(gridLocation), jobService.readAvailableJobs()])
    //   .then(([gridLocationMap, availableJobs]: [ObservableMap<GridLocation>, {[key: string]: string[]}]) => {
    //     jobService.availableJobs = availableJobs;
    //     return gridService.getOrCreateGrid(gridLocation);
    //})
  }

  static contextTypes = {
    router: PropTypes.func.isRequired
  };


  @observable private activeTab: string;
  @observable private infoMetrics: InfoMetricts;
  @observable private logNames: string[];


  componentWillMount() {
    const params = this.context.router.getCurrentParams();
    this.activeTab = (params.tab) ? params.tab : 'info';
  }

  componentDidMount() {
    this[this.activeTab]();
  }

  info() {
    if (!this.infoMetrics) {
      Promise.all([mdAxios.get("/core/info"), mdAxios.get("/manage/info")])
        .then(([responseZzInfo, responseInfo]) => {
          this.infoMetrics = {zzInfo: responseZzInfo.data, info: responseInfo.data};
        });
    }
  }

  logs() {
    if (!this.logNames) {
      mdAxios.get("/core/logging")
        .then((response) => {
          this.logNames = response.data;
        });
    }
  }

  caches() {
    // cache manages its own data
  }


  handleChange = (value: string) => {
    console.log("handleChange " + value);
    this.activeTab = value;
    this[this.activeTab]();

    const routeName = last<Route>(this.context.router.getCurrentRoutes()).name,
      params = this.context.router.getCurrentParams();
    params.tab = value;

    this.context.router.replaceWith(routeName, params, undefined);
  };


  render() {
    let {params, query} = this.props;

    console.debug("ServerPage render");

    return (
      <main className="main-content">
        <Tabs
          value={this.activeTab}
          onChange={this.handleChange}
        >
          <Tab label="Info" value="info">
            <div>
              {(this.infoMetrics) ? (
                <Metrics {...this.infoMetrics}/>
              ) : null}
            </div>
          </Tab>
          <Tab label="Logs" value="logs">
            <div>
              {(this.logNames) ? (
                <Logs data={this.logNames}/>
              ) : null}
            </div>
          </Tab>
          <Tab label="Caches" value="caches">
            <div>
              <CacheStats />
            </div>
          </Tab>
          <Tab label="Threads" value="threads">
            <div>
              <h2>Controllable Tab B</h2>
              <p>
                This is another example of a controllable tab. Remember, if you
                use controllable Tabs, you need to give all of your tabs values or else
                you wont be able to select them.
              </p>
            </div>
          </Tab>
          <Tab label="Connections" value="connections">
            <div>
              <h2>Controllable Tab B</h2>
              <p>
                This is another example of a controllable tab. Remember, if you
                use controllable Tabs, you need to give all of your tabs values or else
                you wont be able to select them.
              </p>
            </div>
          </Tab>
          <Tab label="Beans" value="beans">
            <div>
              <h2>Controllable Tab B</h2>
              <p>
                This is another example of a controllable tab. Remember, if you
                use controllable Tabs, you need to give all of your tabs values or else
                you wont be able to select them.
              </p>
            </div>
          </Tab>

        </Tabs>
      </main>
    );
  }

}




// WEBPACK FOOTER //
// ./src/administration/server/serverOverview.tsx